import { Injectable } from '@angular/core';
import { combineLatest } from "rxjs";
import PipelineView from "../interfaces/pipeline-view";
import PipelineViewFilter from "../interfaces/pipeline-view-filter";
import pipelineViewFilter from "../interfaces/pipeline-view-filter";
import PipelineFieldFilter from "../interfaces/pipeline-field-filter";
import { producerIncrementEpoch } from "@angular/core/primitives/signals";
import { BehaviorSubject, pipe, Observable, lastValueFrom } from "rxjs";
import { Apollo } from "apollo-angular";
import SavedExportField from "../interfaces/saved-export-field";
import {
  CREATE_SAVED_EXPORT,
  CreateSavedExportFieldInput, DELETE_SAVED_EXPORT,
  GET_SAVED_EXPORT,
  GET_SPECIAL_FIELD_VALUES, SavedExportFieldInput,
  UPDATE_SAVED_EXPORT
} from "./graphql/getSavedExports";
import ID from "../types/id";
import {PipelineMilestone} from "../interfaces/pipeline-milestone";
import AttributeType from "../interfaces/attribute-type";
import DateType from "../interfaces/date-type";
import Role from "../interfaces/role";
import Milestone from "../interfaces/milestone";
import RoleType from "../interfaces/role-type";
import id from "../types/id";
import {save_pipeline_filter} from "./graphql/getPipelines";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {AuthService} from "../auth/auth.service";
import {EnvironmentService} from "../environment/environment.service";
import {CachedPipelineDataStore, PipelineExportInput, PipelinesService} from "./pipelines.service";
import {CurrentUserService} from "../services/current-user.service";
import {User} from "../interfaces/user";
import {map} from "rxjs/operators";
export type LabeledField = {
  type?: string;
  parent?: LabeledField|null;
  selectedChildren?: any;
  excludedType?: boolean;
} & PipelineFieldFilter

@Injectable({
  providedIn: 'root',
})
export class PipelineExportService {
  _defaultFields:LabeledField[] = [];
  defaultFields = new BehaviorSubject(this._defaultFields)
  defaultFields$ = this.defaultFields.asObservable()

  _formattedFields:LabeledField[] = [];
  formattedFields = new BehaviorSubject(this._formattedFields)
  formattedFields$ = this.formattedFields.asObservable()

  _savedExportFields: SavedExportField[] = [];
  savedExportFields = new BehaviorSubject(this._savedExportFields)
  savedExportFields$ = this.savedExportFields.asObservable()

  _loading = false;
  loading = new BehaviorSubject(this._loading)
  loading$ = this.loading.asObservable()

  private _attributeTypes: AttributeType[] = [];
  private attributeTypes = new BehaviorSubject(this._attributeTypes);
  public attributeTypes$ = this.attributeTypes.asObservable();

  private _dateTypes:DateType[] = [];
  private dateTypes = new BehaviorSubject(this._dateTypes);
  public dateTypes$ = this.dateTypes.asObservable();

  private _dealParticipants: Role[]=[];
  private dealParticipants = new BehaviorSubject(this._dealParticipants);
  public dealParticipants$ = this.dealParticipants.asObservable();

  private _fundParticipants: Role[]=[];
  private fundParticipants = new BehaviorSubject(this._fundParticipants);
  public fundParticipants$ = this.fundParticipants.asObservable();

  private _milestones: Milestone[]=[];
  private milestones = new BehaviorSubject(this._milestones);
  public milestones$ = this.milestones.asObservable();

  excludedFieldTypes = ['attributes', 'participants','fund_participants', 'milestones', 'project_dates'];
  specialTypes = {
    combined_columns:{
      type:'combined_columns',
      name:' as Combined Columns'
    },
    as_columns:{
      type:'as_columns',
      name:' as Columns'
    },
    summary:{
      type:'summary',
      name: ' Detailed Summary'
    },
    list: {
      type:'list',
      name:' List'
    }
  };

  public constructor(public apollo: Apollo, public http: HttpClient, public auth:AuthService, public env:EnvironmentService, public pipelineService: PipelinesService, public currentUser: CurrentUserService) {
  }

  fixMismatchedNames(name:string){
    switch(name){
      case 'Outlets':
        return 'Outlets List';
      case 'Sponsor/Developer':
        return 'Developer';
      case 'Credit Types':
        return 'Tax Credits Detailed Summary';
      case 'Project Name':
        return 'Property Name';
      case 'LT Yield':
        return 'Lower Tier Yield';
      case 'UT Yield':
        return 'Upper Tier Yield - LT Projected';
      case 'Total LT Equity':
        return 'Total Equity';
      case 'LT Equity':
        return 'Equity';
      case '4%/9%':
        return 'Credit Designation';

      default:
        return name;
    }
  }

  private _setMilestones(pipelineMilestones:PipelineMilestone[]){
    this._milestones = pipelineMilestones
      .slice()
      .sort((a,b) => a.index - b.index)
      .map((pipelineMilestone)=>pipelineMilestone.milestone)
      .filter(milestone => milestone) as Milestone[];
    this.milestones.next(this._milestones)
  }

  private _setFundParticipants(fundParticipants: RoleType){
    this._fundParticipants = fundParticipants.roles?.slice().sort((a,b) =>
      a.name.localeCompare(b.name)
    ) ?? [];
    this.fundParticipants.next(this._fundParticipants);
  }
  private _setDealParticipants(dealParticipants: RoleType, sponsors:RoleType){
    if(sponsors.roles && dealParticipants.roles){
    this._dealParticipants = [...dealParticipants.roles, ...sponsors.roles].sort((a,b) =>
      a.name.localeCompare(b.name)
      ) ?? [];
      this.dealParticipants.next(this._dealParticipants);
    }
  }
  private _setDateTypes(dateTypes: DateType[]){
    this._dateTypes = dateTypes.slice().sort((a,b) =>
      a.name.localeCompare(b.name)
    );
    this.dateTypes.next(this._dateTypes);
  }
  private _setAttributeTypes(attributeTypes:AttributeType[]){
    this._attributeTypes = attributeTypes;
    this.attributeTypes.next(this._attributeTypes);
  }
  private _setSavedExportFields(savedExportFields: SavedExportField[]) {
    this._savedExportFields = savedExportFields
    this.savedExportFields.next(this._savedExportFields);
  }
  private _setLoading(loading: boolean) {
    this._loading = loading;
    this.loading.next(this._loading);
  }
  public getSavedExportFields(userId: ID){
    this._setLoading(true);
    let sub = combineLatest({
      savedExports: this.apollo.query({
        query:GET_SAVED_EXPORT,
        variables:{
          userId
        },
        fetchPolicy:"network-only"
      }),
      specialFields: this.apollo.query({
        query:GET_SPECIAL_FIELD_VALUES,
      })
    }).subscribe(({savedExports, specialFields}) => {
        if(savedExports.data?.getSavedExports) {
          this._setSavedExportFields(savedExports.data.getSavedExports);
        }
        if(specialFields.data){
          this._setAttributeTypes(specialFields.data.attribute_types);
          this._setDateTypes(specialFields.data.date_types);
          this._setFundParticipants(specialFields.data.fundParticipants);
          this._setDealParticipants(specialFields.data.dealParticipants, specialFields.data.sponsors);
          this._setMilestones(specialFields.data.milestones);
        }
        this._setLoading(false);
        sub?.unsubscribe();
      })

  }

  private _setFormattedFields(formattedFields:LabeledField[]){
    this._formattedFields = formattedFields;
    this.formattedFields.next(this._formattedFields);
  }
  private _setDefaultFields(defaultFields:LabeledField[]){
    this._defaultFields = defaultFields;
    this.defaultFields.next(this._defaultFields);
  }
  private formatForChild(parent:LabeledField,fieldPrototype: {id: ID, name: string}):LabeledField{
    return {
      id:fieldPrototype.id,
      name:fieldPrototype.name,
      field:'',
      id_field:'',
      json_parent_id: parent.id,
      format:'',
      is_typeahead:false,
      is_multiselect:false,
      is_filterable:false,
      is_exportable:true,
      is_sortable:false,
      is_grouped:false,
      sort_type:'',
      model:'',
      special_lookup:false,
      example:null,
      summary_example:null,
      as_combined_columns_example:null,
      as_columns_example: null,
      children: [],
      parent: {...parent, children:[]}
    }
  }
  getChildrenForExcludedType(field: LabeledField):LabeledField[]{
    switch(field.field){
      case 'attributes':
        return this._attributeTypes.slice().map(type=>this.formatForChild(field, type));
      case 'participants':
        return this._dealParticipants.slice().map(participant=>this.formatForChild(field, participant));
      case 'fund_participants':
        return this._fundParticipants.slice().map(participant=>this.formatForChild(field, participant));
      case 'milestones':
        return this._milestones.slice().map(milestone=>this.formatForChild(field, milestone));
      case 'project_dates':
        return this._dateTypes.slice().map(date=>this.formatForChild(field, date));
      default:
        return [];
    }
  }

  expandFields(allFields:PipelineFieldFilter[]):LabeledField[]
  {
    let fields = [];
    for (let i=0; i< allFields.length; i++){
      let field = allFields[i];
      let newVal:LabeledField = {...field, children: field.children.filter((child:PipelineFieldFilter)=>child.is_exportable).slice()};
      if(field.children && field.children.length){
        if(newVal.hasOwnProperty('field') && newVal.field !== null && !(this.excludedFieldTypes).includes(newVal.field)){
          //account for multiple format types here
          let newVal1:LabeledField = {...newVal};
          let newVal2:LabeledField = {...newVal};
          let newVal3:LabeledField = {...newVal};
          newVal.type = this.specialTypes.as_columns.type;
          newVal.name += this.specialTypes.as_columns.name;
          newVal.selectedChildren = {}
          newVal.children.forEach((child)=>{
            newVal.selectedChildren[child.id] = false;
          })

          newVal1.name += this.specialTypes.combined_columns.name;
          newVal1.type = this.specialTypes.combined_columns.type;
          newVal1.children = newVal1.children.map((child:LabeledField) => ({...child, parent: {...newVal1, children:[]}}));

          newVal2.children = [];
          newVal2.name += this.specialTypes.summary.name;
          newVal2.type = this.specialTypes.summary.type;


          fields.push(newVal);
          fields.push(newVal1);
          fields.push(newVal2);
          if(newVal.hasOwnProperty('example') && newVal.example) {
            newVal3.children = [];
            newVal3.name += this.specialTypes.list.name;
            newVal3.type = this.specialTypes.list.type;
            fields.push(newVal3);
          }
        }else{
          newVal.excludedType = true;
          newVal.children = this.getChildrenForExcludedType(newVal);
          fields.push(newVal);
        }
      }else{
        fields.push(newVal);
      }
    }
    return fields;
  };
  getDefaultFields(pipelineView: PipelineView, fetchedFields: PipelineFieldFilter[]):void
  {
    //copy fields for mutability
    this._setFormattedFields(this.expandFields([...fetchedFields].filter((field:PipelineFieldFilter)=>field.is_exportable)));
    let expandedFields = this._formattedFields;
    let defaultFields:LabeledField[] = []
    let fields = [...(typeof pipelineView.default_fields === 'string' ?  JSON.parse(pipelineView.default_fields) : pipelineView.default_fields)];
    if(fields){
      for (let i = 0; i < fields.length; i++) {
        let field = fields[i];
        let value: LabeledField|null = null;
        // var isChild = false;

        let needle = this.fixMismatchedNames(field.display_name);
        if(needle === 'Location'){
          fields[i].display_name = 'City';
          fields.splice(i+1, 0, {display_name:'State'});
          i--;
          continue;
        }
        // handle mismatched names
        for (let j = 0; j < expandedFields.length; j++) {
          let fField = expandedFields[j];
          if (fField.name === needle) {
            value = fField;
            //@todo need to check off all the children in this field against all of the defaults simultaneously
          } else if (fField.children && fField.children.length && fField.type !== this.specialTypes.as_columns.type) {
            field = {...fField};
            field.children = [];
            let child:LabeledField|null = null;
            for (let k = 0; k < fField.children.length; k++ ) {
              let fChild:LabeledField = fField.children[k];
              if(fChild.name === needle){
                child= fChild;
                field.children.push(fChild);
              }
            }
            if (field.children.length && child) {
              value = {...child};
              if(value) {
                value.parent = { ...fField, children:[]};
              }
            }
          }
        }
        if(value) {
          defaultFields.push({ ...value });
        }
      }
    }
    this._setDefaultFields(defaultFields);
  }

  public exportParams(cachedData:CachedPipelineDataStore|null, columns:LabeledField[]):PipelineExportInput{
    return {
      filter: typeof cachedData?.filter === 'string' ? JSON.parse(cachedData?.filter ?? '[]') ?? [] : cachedData?.filter,
      sort: typeof cachedData?.sort === 'string' ? JSON.parse(cachedData?.sort ?? '[]') ?? [] : cachedData?.sort,
      group: typeof cachedData?.group === 'string' ? JSON.parse(cachedData?.group ?? '[]') ?? [] : cachedData?.group,
      pipeline_view_id: cachedData?.pipeline_view_id,
      pipeline_view_filter_id: cachedData?.pipeline_view_filter_id,
      columns
    }
  }


  public exportPipeline({filter,sort,group,pipeline_view_id,pipeline_view_filter_id, columns}: PipelineExportInput){
    return new Promise<void>((resolve,reject) => {
      let url = this.env.api_url + "/" + 'export_pipeline'
      const headers = new HttpHeaders({"Authorization": this.auth.getToken()})
      let body: any = {
        filter,
        sort,
        group,
        pipeline_view_id,
        pipeline_view_filter_id,
        columns
      }
      let sub = this.http.post(url, body, {
        responseType: 'blob',
        headers
      }).subscribe((response: Blob) => {
        let downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(response);
        downloadLink.setAttribute('export', 'export.xlsx');
        downloadLink.setAttribute('target', '_blank');
        document.body.appendChild(downloadLink);
        downloadLink.click();
        resolve();
        sub?.unsubscribe();
      });
    })
  }

  public quickExport(cachedData:CachedPipelineDataStore | null, currentBaseView:PipelineView|null, fields:PipelineFieldFilter[]){
    if(cachedData && currentBaseView){

      let userSub = this.currentUser.user$.subscribe((user:User)=> {
        if(user?.id){
          this.getSavedExportFields(user.id);
          userSub?.unsubscribe();
        }
      });
      let sub = combineLatest({
        attributeTypes:this.attributeTypes$,
        dateTypes:this.dateTypes$,
        dealParticipants:this.dealParticipants$,
        fundParticipants:this.fundParticipants$,
        milestones:this.milestones$,
      }).subscribe(({attributeTypes,dealParticipants,dateTypes,fundParticipants,milestones})=> {
        if(attributeTypes.length && dealParticipants.length && dateTypes.length && fundParticipants.length && milestones.length){
          this.getDefaultFields(currentBaseView, fields)
          sub?.unsubscribe();
        }
      });
    }
    let fieldSub = this.defaultFields$.subscribe((defaultFields:LabeledField[])=> {
      if(defaultFields.length) {
        this.exportPipeline(this.exportParams(cachedData, defaultFields));
        fieldSub?.unsubscribe();
      }
    });
  }

  refreshSavedExports(userId:ID){
    let sub = this.apollo.query(
      {
        query:GET_SAVED_EXPORT,
        variables:{
          userId
        },
        fetchPolicy:"network-only"
      }
    ).subscribe(({data})=>{
      if(data?.getSavedExports){
        this._setSavedExportFields(data.getSavedExports);
        sub?.unsubscribe();
      }
    })
  }

  createSavedExportField(input:CreateSavedExportFieldInput){
    let sub = this.apollo.mutate({
        mutation:CREATE_SAVED_EXPORT,
        variables:{
          input
        }
      }
    ).subscribe(({data})=>{
      if(data?.createSavedExport?.user_id){
        this.refreshSavedExports(data.createSavedExport.user_id);
        sub?.unsubscribe();
      }
    });
  }
  updateSavedExportField(input:SavedExportFieldInput, id:ID){
    let sub = this.apollo.mutate({
      mutation:UPDATE_SAVED_EXPORT,
      variables:{
        input,
        id
      }
    }).subscribe(({data})=>{
      if(data?.updateSavedExport){
        this.refreshSavedExports(data.updateSavedExport.user_id);
        sub?.unsubscribe();
      }
    })
  }

  deleteSavedExportField(id:ID){
    let sub = this.apollo.mutate({
      mutation:DELETE_SAVED_EXPORT,
      variables:{
        id
      }
    }).subscribe(({data})=>{
      if(data?.deleteSavedExport){
        this.refreshSavedExports(data.deleteSavedExport.user_id);
        sub?.unsubscribe();
      }
    })
  }
}
